import { html, nothing } from 'lit';
import { t } from 'src/i18n';

import { customElement, property } from 'lit/decorators.js';
import 'src/content/entity-content';
import '../../library/editors/index.js';
import '../../library/editors/elements/d-select-checkbox.js';
import '../../library/fields/index.js';
import '../../library/fields/d-expansion.js';
import '../../library/fields/d-view-html.js';
import '../../library/lists/d-list-section-attachment.js';
import '../../pages/computers-page/d-edit-single-related-item.js';
import { DataMapElement } from './data-map-element.js';
import './d-view-checklist.js';
import './d-view-checkpoint.js';
import './d-view-related-items.js';
import type { Network } from './network';
import type { PrinterEditItem } from './printer-edit-item';
import type { ComputerMapPrinter } from './computer-map-printer';
import type { SelectDropdownOption } from '../../library/editors/elements/select-dropdown-option';

/**
 *
 */
@customElement('d-computer-unit-printer')
export class DComputerUnitPrinter extends DataMapElement<ComputerMapPrinter, PrinterEditItem> {
  static readonly styles = [...DataMapElement.styles];
  printerPositionSecureOptions = [
    {
      value: 'true',
      text: t('computers.printerPositionSecure'),
    },
  ];
  @property({ type: Array })
  networks: Network[] = [];

  initializeEditItem(): PrinterEditItem {
    return {
      name: this.item.name,
      networkUuid: this.item.networkUuid,
      notes: this.item.notes,
      printerPositionSecure: this.item.printerPositionSecure,
      location: this.item.location,
    };
  }

  _displayTypeName(): string {
    return t('computers.printer.typeName');
  }

  _displayName() {
    return this.item.name;
  }

  _operatorTypes(networkConnection) {
    const list = [
      { value: 'UNDEFINED', text: t('computers.options.operatorChoose') },
      { value: 'NONE', text: t('computers.options.none') },
    ];
    if (networkConnection !== 'NONE' && networkConnection !== '') {
      list.push({ value: 'INHERIT', text: t('computers.options.operatorInheritNetwork') });
    }
    list.push(
      { value: 'internal', text: t('computers.options.operatorInternal') },
      { value: 'external', text: t('computers.options.operatorExternal') },
      {
        value: 'internalAndExternal',
        text: t('computers.options.operatorInternalAndExternal'),
      },
    );
    return list;
  }

  _getOperatorTypeValue(id) {
    let result = '';
    const list = this._operatorTypes(this.item.networkUuid);
    for (const item of list) {
      if (id === item.value) {
        result = item.text;
      }
    }

    if (result === t('computers.options.operatorChoose')) {
      result = t('computers.options.notSelected');
    }

    return result;
  }

  _isValid(): boolean {
    return true;
  }

  renderUnitView() {
    return html`<section view="">
      <d-section>
        ${!this.item.location
          ? nothing
          : html`<d-view-text field="printers_location" value="${this.item.location}"> </d-view-text>`}
      </d-section>

      ${!this.item.notes
        ? nothing
        : html`<d-section>
            <d-view-html field="printers_notes" value="${this.item.notes}"> </d-view-html>
          </d-section>`}
      <d-list-section-attachment
        .attachments="${this.item.attachments}"
        class="computers"
        label=${t('computers.attachments')}
        page-id="20"
        sort="1"
        .writeAccess=${this.writeAccess}
        theme-page
      >
      </d-list-section-attachment>
    </section>`;
  }

  renderUnitEdit(editItem: PrinterEditItem) {
    return html`
      <d-section>
        <d-select-dropdown
          theme-page
          field="printers_network"
          .options="${this._networkConnections(this.networks)}"
          value="${editItem.networkUuid}"
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              networkUuid: e.detail.value,
            };
          }}
        >
        </d-select-dropdown>
        <d-edit-text
          theme-page
          field="printers_name"
          value="${editItem.name}"
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              name: e.detail.value,
            };
          }}
        >
        </d-edit-text>
      </d-section>
      <d-section>
        <d-edit-text
          theme-page
          field="printers_location"
          placeholder=${t('computers.placeholder.describePrinterLocation')}
          value="${editItem.location}"
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              location: e.detail.value,
            };
          }}
        >
        </d-edit-text>
      </d-section>
      <d-section topless>
        <d-select-checkbox
          theme-page
          deselectable="true"
          field=""
          .options=${this.printerPositionSecureOptions}
          single
          .value=${[editItem.printerPositionSecure ? 'true' : 'false']}
          @value-changed=${(e: CustomEvent<{ value: string }>) => {
            e.stopPropagation();
            this.editItem = {
              ...editItem,
              printerPositionSecure: e.detail.value === 'true',
            };
          }}
        >
        </d-select-checkbox>
      </d-section>
      <d-edit-html
        theme-page
        map-element
        .contentStickyTop=${this.contentStickyTop}
        field="printers_notes"
        value="${editItem.notes}"
        @value-changed=${(e: CustomEvent<{ value: string }>) => {
          e.stopPropagation();
          this.editItem = {
            ...editItem,
            notes: e.detail.value,
          };
        }}
      >
      </d-edit-html>
    `;
  }

  protected typeClasses(): string {
    return '';
  }

  private _networkConnections(networks: { name: string; uuid: string }[]): SelectDropdownOption[] {
    const list = [
      { value: '', text: t('computers.placeholder.chooseNetworkConnection') },
      { value: 'NONE', text: t('computers.options.noNetworkConnection') },
    ];
    let unregisteredLocalNetworkTerm = t('computers.localNetwork');
    if (networks.length > 0) {
      unregisteredLocalNetworkTerm = t('computers.otherLocalNetwork');
      for (const element of networks) {
        let value = t('computers.localNetworkWithoutName');
        if (element.name !== '') {
          value = t('computers.localNetworkNamed', { name: element.name });
        }
        list.push({ value: element.uuid, text: value });
      }
    }
    list.push({ value: 'OTHER', text: unregisteredLocalNetworkTerm });
    return list;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-computer-unit-printer': DComputerUnitPrinter;
  }
}
